// src/pages/publicRCData/rcRiskModelCalculations.js
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../utils/firebase";

// Add this RIGHT AFTER your imports
const MINIMUM_SAMPLE_SIZE = 15; // Tighter - was 10
const MINIMUM_RISK_DIFFERENCE = 0.15; // NEW - require 15% difference
const MINIMUM_PREVALENCE = 0.05;
const MAXIMUM_PREVALENCE = 0.9; // Tighter - was 0.95

// Helper function to calculate days between dates
const calculateDaysBetween = (startDate, endDate = new Date()) => {
  if (!startDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  const diffTime = Math.abs(end - start);
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

// Helper function to extract the correct field values
// Replace your extractResidentDisplayData function with this enhanced version:

const extractResidentDisplayData = (resident) => {
  console.log(
    `\n🔍 EXTRACTING DISPLAY DATA FOR: ${resident.residentName || "Unknown"}`
  );

  // Show what data we have available
  console.log(`📊 Available fields:`, {
    residentName: resident.residentName,
    fullName: resident.fullName,
    recoveryCapitalTotal: resident.recoveryCapitalTotal,
    dob: resident.dob,
    sobrietyDate: resident.sobrietyDate,
    alumniSobrietyDate: resident.alumniSobrietyDate,
    currentResident: resident.currentResident,
    alumniSobrietyStatus: resident.alumniSobrietyStatus,
  });

  // Recovery Capital score (should already be joined from /recoveryCapital)
  const rcScore = resident.recoveryCapitalTotal || 0;

  // Calculate days sober from sobrietyDate (try multiple sources)
  let sobrietyDate = resident.sobrietyDate || resident.alumniSobrietyDate;
  const daysSober = sobrietyDate ? calculateDaysBetween(sobrietyDate) : 0;

  console.log(`📅 Sobriety calculation:`, {
    sobrietyDate: sobrietyDate,
    daysSober: daysSober,
    years: (daysSober / 365.25).toFixed(1),
  });

  // Calculate age from dob
  const age = resident.dob ? calculateDaysBetween(resident.dob) / 365.25 : 0;

  console.log(`🎂 Age calculation:`, {
    dob: resident.dob,
    ageInDays: resident.dob ? calculateDaysBetween(resident.dob) : 0,
    age: Math.floor(age),
  });

  // Use fullName from residents collection
  const residentName =
    resident.fullName || resident.residentName || "Unknown Resident";

  // Generate a consistent ID from name if needed
  const residentId =
    resident.id ||
    resident._id ||
    residentName.toLowerCase().replace(/\s+/g, "-");

  const result = {
    recoveryCapitalTotal: Number(rcScore) || 0,
    daysSoberAtAssessment: Number(daysSober) || 0,
    age: Math.floor(Number(age)) || 0,
    residentId,
    residentName,
  };

  console.log(`✅ Final extracted data:`, result);

  // Flag potential data issues
  if (result.recoveryCapitalTotal === 0) {
    console.log(`⚠️ WARNING: No recovery capital score found`);
  }
  if (result.daysSoberAtAssessment === 0) {
    console.log(`⚠️ WARNING: No sobriety date found`);
  }
  if (result.age === 0) {
    console.log(`⚠️ WARNING: No date of birth found`);
  }

  return result;
};

/**
 * Calculate recovery time protection with sample size validation
 * Falls back to previous time buckets when sample size is insufficient
 */
const calculateRecoveryTimeProtection = (historicalData, alumniMap) => {
  console.log(
    `🔍 DEBUG: calculateRecoveryTimeProtection called with ${historicalData.length} residents`
  );
  console.log(`\n📊 DEBUGGING DAYS SOBER CALCULATION:`);
  historicalData.slice(0, 5).forEach((resident, i) => {
    console.log(`  Resident ${i + 1}: "${resident.residentName}"`);
    console.log(`    daysSoberAtAssessment: ${resident.daysSoberAtAssessment}`);
    console.log(`    sobrietyDate: ${resident.sobrietyDate}`);
    console.log(`    alumniSobrietyDate: ${resident.alumniSobrietyDate}`);
    console.log(`    dob: ${resident.dob}`);
    console.log(
      `    Raw resident object keys: ${Object.keys(resident).join(", ")}`
    );
  });
  console.log(`🔍 DEBUG: alumniMap size: ${alumniMap.size}`);
  console.log(
    "\n🛡️ CALCULATING RECOVERY TIME PROTECTION WITH SAMPLE SIZE VALIDATION..."
  );

  // Define time buckets with clear progression
  const timeBuckets = [
    { name: "0-90 days", min: 0, max: 90, baselineRisk: true },
    { name: "91-180 days", min: 91, max: 180 },
    { name: "181-365 days", min: 181, max: 365 },
    { name: "1-2 years", min: 366, max: 730 },
    { name: "2-3 years", min: 731, max: 1095 },
    { name: "3+ years", min: 1096, max: 99999 },
  ];

  // Calculate raw rates for each bucket
  const bucketData = timeBuckets.map((bucket) => {
    const residents = historicalData.filter((resident) => {
      const days = resident.daysSoberAtAssessment || 0;
      return days >= bucket.min && days <= bucket.max;
    });

    if (residents.length === 0) {
      return {
        ...bucket,
        residents: [],
        sampleSize: 0,
        relapses: 0,
        relapseRate: null,
        reliable: false,
        useFallback: true,
      };
    }

    const relapses = residents.filter((resident) => {
      const residentName =
        resident.residentName ||
        resident.fullName ||
        `${resident.firstName} ${resident.lastName}`.trim();
      const alumni = alumniMap.get(residentName);
      return alumni && alumni.sobrietyStatus === "Not Sober";
    }).length;

    const relapseRate = relapses / residents.length;
    const reliable = residents.length >= MINIMUM_SAMPLE_SIZE;

    console.log(
      `  ${bucket.name}: ${
        residents.length
      } residents, ${relapses} relapses (${(relapseRate * 100).toFixed(1)}%) ${
        reliable ? "✅ RELIABLE" : "⚠️ SMALL SAMPLE"
      }`
    );

    return {
      ...bucket,
      residents,
      sampleSize: residents.length,
      relapses,
      relapseRate,
      reliable,
      useFallback: !reliable && !bucket.baselineRisk,
    };
  });

  // Apply PROPER cascading fallback logic
  const validatedRates = [];

  bucketData.forEach((bucket, index) => {
    if (bucket.baselineRisk) {
      // First bucket is always the baseline (no protection)
      validatedRates.push({
        ...bucket,
        finalRate: bucket.relapseRate || 0.4, // Default if no data
        protectionFactor: 0,
        fallbackUsed: false,
        fallbackSource: null,
      });
    } else if (bucket.reliable) {
      // Bucket has sufficient sample size
      const baselineRate = validatedRates[0].finalRate;
      const protectionFactor = Math.max(0, baselineRate - bucket.relapseRate);

      validatedRates.push({
        ...bucket,
        finalRate: bucket.relapseRate,
        protectionFactor,
        fallbackUsed: false,
        fallbackSource: null,
      });
    } else {
      // 🔥 PROPER CASCADING: Look backwards for ANY data (reliable or not)
      let fallbackRate = null;
      let fallbackSource = "baseline";
      let fallbackBucket = null;

      // Go backwards through previous buckets, looking for ANY available data
      for (let lookBack = index - 1; lookBack >= 0; lookBack--) {
        const previousBucketData = bucketData[lookBack];
        if (previousBucketData && previousBucketData.relapseRate !== null) {
          // Found data in a previous bucket - use it regardless of sample size
          fallbackRate = previousBucketData.relapseRate;
          fallbackSource = previousBucketData.name;
          fallbackBucket = previousBucketData;
          console.log(
            `    🔄 CASCADE: ${bucket.name} → using ${fallbackSource} (${
              previousBucketData.sampleSize
            } people, ${(fallbackRate * 100).toFixed(1)}% rate)`
          );
          break;
        }
      }

      // If still no rate found anywhere, use baseline
      if (fallbackRate === null) {
        fallbackRate = validatedRates[0].finalRate;
        fallbackSource = "baseline";
        console.log(
          `    🔄 BASELINE FALLBACK: ${bucket.name} → using baseline (no prior data found)`
        );
      }

      // If current bucket has some data, decide whether to use it or the fallback
      let finalRate = fallbackRate;
      let finalSource = fallbackSource;
      let usedFallback = true;

      if (bucket.relapseRate !== null) {
        // Current bucket has some data
        if (bucket.sampleSize >= 5) {
          // If we have at least 5 people, prefer current bucket data
          finalRate = bucket.relapseRate;
          finalSource = `${bucket.name} (${bucket.sampleSize} people)`;
          usedFallback = false;
          console.log(
            `    ✅ USING CURRENT: ${bucket.name} has ${
              bucket.sampleSize
            } people, using its ${(finalRate * 100).toFixed(1)}% rate`
          );
        } else if (bucket.relapseRate < fallbackRate) {
          // Very small sample but shows improvement - use it
          finalRate = bucket.relapseRate;
          finalSource = `${bucket.name} (${bucket.sampleSize} people, limited data)`;
          usedFallback = false;
          console.log(
            `    ⚠️ SMALL SAMPLE BETTER: ${bucket.name} has only ${
              bucket.sampleSize
            } people but better rate (${(finalRate * 100).toFixed(1)}%)`
          );
        } else {
          console.log(
            `    📉 USING FALLBACK: ${bucket.name} has ${
              bucket.sampleSize
            } people with ${(bucket.relapseRate * 100).toFixed(
              1
            )}% rate, but using ${fallbackSource} ${(
              fallbackRate * 100
            ).toFixed(1)}% instead`
          );
        }
      }

      const baselineRate = validatedRates[0].finalRate;
      const protectionFactor = Math.max(0, baselineRate - finalRate);

      validatedRates.push({
        ...bucket,
        finalRate,
        protectionFactor,
        fallbackUsed: usedFallback,
        fallbackSource: finalSource,
        reliability:
          bucket.sampleSize >= MINIMUM_SAMPLE_SIZE
            ? "high"
            : bucket.sampleSize >= 10
            ? "moderate"
            : bucket.sampleSize >= 5
            ? "low"
            : "very_low",
      });
    }
  });

  // Convert to lookup table for easy access
  const protectionLookup = new Map();
  validatedRates.forEach((bucket) => {
    for (let days = bucket.min; days <= bucket.max; days++) {
      protectionLookup.set(days, {
        bucket: bucket.name,
        relapseRate: bucket.finalRate,
        protectionFactor: bucket.protectionFactor,
        reliable: bucket.reliable,
        fallbackUsed: bucket.fallbackUsed,
        fallbackSource: bucket.fallbackSource,
      });
    }
  });

  console.log(`\n📊 FINAL PROTECTION FACTORS:`);
  validatedRates.forEach((bucket) => {
    const reliability = bucket.reliable
      ? "HIGH"
      : bucket.fallbackUsed
      ? "FALLBACK"
      : "MODERATE";
    console.log(
      `  ${bucket.name}: ${(bucket.protectionFactor * 100).toFixed(
        1
      )}% protection (${reliability} confidence)`
    );
  });

  return {
    bucketData: validatedRates,
    protectionLookup,
    baselineRate: validatedRates[0].finalRate,
  };
};

/**
 * Get recovery time protection for a specific number of days
 */
const getRecoveryTimeProtection = (daysSober, protectionData) => {
  const clampedDays = Math.min(daysSober, 9999); // Cap at reasonable max
  const protection = protectionData.protectionLookup.get(clampedDays);

  if (!protection) {
    // Default for very high values
    return {
      protectionFactor: 0.3, // Moderate protection
      bucket: "3+ years",
      reliable: false,
      fallbackUsed: true,
      fallbackSource: "default",
    };
  }

  return protection;
};

/**
 * CORE DATA FETCHING - Shared by all components
 */
// Replace your fetchCoreRCData function with this:
const fetchCoreRCData = async (organizationId) => {
  if (!organizationId) throw new Error("Organization ID is required");

  // Fetch all raw data (same as your existing logic)
  const rcSnapshot = await getDocs(
    collection(db, `organizations/${organizationId}/recoveryCapital`)
  );
  const rawData = rcSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  const alumniSnapshot = await getDocs(
    collection(db, `organizations/${organizationId}/alumni`)
  );
  const alumniMap = new Map();
  alumniSnapshot.docs.forEach((d) => {
    const data = d.data();
    const fullName =
      data.fullName ||
      data.residentName ||
      `${data.firstName} ${data.lastName}`.trim();
    alumniMap.set(fullName, {
      ...data,
      originalId: d.id,
      sobrietyStatus: data.sobrietyStatus,
      dischargeDate: data.dischargeDate,
      sobrietyDate: data.sobrietyDate,
    });
  });

  const residentsSnapshot = await getDocs(
    collection(db, `organizations/${organizationId}/residents`)
  );
  const residentsMap = new Map();
  residentsSnapshot.docs.forEach((d) => {
    const data = d.data();
    const fullName =
      data.fullName ||
      data.residentName ||
      `${data.firstName} ${data.lastName}`.trim();
    residentsMap.set(fullName, {
      ...data,
      originalId: d.id,
      sobrietyDate: data.sobrietyDate,
      currentResident: data.currentResident,
      residentName: fullName,
    });
  });

  // 🔥 NEW: JOIN THE DATA BEFORE FILTERING WITH ENHANCED DEBUGGING
  console.log("\n🔗 JOINING RECOVERY CAPITAL DATA WITH RESIDENTS DATA...");
  console.log(
    `📊 Data to join: ${rawData.length} RC records, ${residentsMap.size} residents, ${alumniMap.size} alumni`
  );

  // First, let's see what names we have in each collection
  console.log("\n📝 Sample RC residentNames:");
  rawData.slice(0, 5).forEach((rc) => {
    console.log(`  "${rc.residentName}" (RC ID: ${rc.id})`);
  });

  console.log("\n📝 Sample Resident fullNames:");
  Array.from(residentsMap.keys())
    .slice(0, 5)
    .forEach((name) => {
      console.log(`  "${name}"`);
    });

  console.log("\n📝 Sample Alumni fullNames:");
  Array.from(alumniMap.keys())
    .slice(0, 5)
    .forEach((name) => {
      console.log(`  "${name}"`);
    });

  const joinedData = rawData.map((rcRecord) => {
    const residentName = rcRecord.residentName;

    // Try to find matching resident by name
    const residentData = residentsMap.get(residentName);
    const alumniData = alumniMap.get(residentName);

    // Enhanced matching - try different name variations if exact match fails
    let matchedResidentData = residentData;
    let matchedAlumniData = alumniData;

    if (!residentData && !alumniData) {
      // Try fuzzy matching
      const rcNameClean = residentName?.toLowerCase().trim();

      // Check residents map
      for (let [fullName, data] of residentsMap) {
        if (fullName.toLowerCase().trim() === rcNameClean) {
          matchedResidentData = data;
          console.log(
            `🔄 Fuzzy matched resident: "${residentName}" → "${fullName}"`
          );
          break;
        }
      }

      // Check alumni map
      for (let [fullName, data] of alumniMap) {
        if (fullName.toLowerCase().trim() === rcNameClean) {
          matchedAlumniData = data;
          console.log(
            `🔄 Fuzzy matched alumni: "${residentName}" → "${fullName}"`
          );
          break;
        }
      }
    }

    if (matchedResidentData || matchedAlumniData) {
      console.log(`✅ Joined RC data for "${residentName}":`, {
        rcTotal: rcRecord.recoveryCapitalTotal,
        dob: matchedResidentData?.dob,
        sobrietyDate:
          matchedResidentData?.sobrietyDate || matchedAlumniData?.sobrietyDate,
        currentResident: matchedResidentData?.currentResident,
        alumniStatus: matchedAlumniData?.sobrietyStatus,
      });
    } else {
      console.log(`⚠️ NO MATCH found for RC resident "${residentName}"`);
    }

    return {
      ...rcRecord, // RC data (has recoveryCapitalTotal, residentName, etc.)
      ...matchedResidentData, // Resident data (has dob, sobrietyDate, fullName, etc.)
      // Merge alumni data too for sobriety info
      alumniSobrietyStatus: matchedAlumniData?.sobrietyStatus,
      alumniSobrietyDate: matchedAlumniData?.sobrietyDate,
      alumniDischargeDate: matchedAlumniData?.dischargeDate,
      // Preserve important fields that might get overwritten
      recoveryCapitalTotal: rcRecord.recoveryCapitalTotal,
      residentName: rcRecord.residentName,
      rcRecordId: rcRecord.id,
      residentRecordId: matchedResidentData?.originalId,
      alumniRecordId: matchedAlumniData?.originalId,
    };
  });

  console.log(
    `🔗 Joined ${joinedData.length} RC records with resident/alumni data`
  );

  // Count successful joins
  const successfulJoins = joinedData.filter(
    (record) => record.dob || record.sobrietyDate || record.alumniSobrietyStatus
  ).length;
  console.log(
    `✅ Successful data joins: ${successfulJoins}/${
      joinedData.length
    } (${Math.round((successfulJoins / joinedData.length) * 100)}%)`
  );

  // Show examples of successfully joined records
  console.log(`\n📋 Examples of successfully joined records:`);
  joinedData
    .filter((record) => record.dob || record.sobrietyDate)
    .slice(0, 3)
    .forEach((record) => {
      console.log(
        `  "${record.residentName}": RC=${record.recoveryCapitalTotal}, DOB=${record.dob}, Sober=${record.sobrietyDate}`
      );
    });

  // Now filter from the JOINED data instead of raw RC data
  const historicalData = joinedData.filter((row) => {
    const residentName = row.residentName;
    const alumni = alumniMap.get(residentName);
    return alumni && alumni.sobrietyStatus;
  });

  const currentResidentData = joinedData.filter((row) => {
    const residentName = row.residentName;
    const resident = residentsMap.get(residentName);
    const alumni = alumniMap.get(residentName);
    return resident?.currentResident === true || !alumni?.sobrietyStatus;
  });

  const getLatestAssessmentPerResident = (data) => {
    const residentMap = new Map();
    data.forEach((row) => {
      const residentName =
        row.residentName ||
        row.fullName ||
        `${row.firstName} ${row.lastName}`.trim() ||
        row.residentId;
      const assessmentDate =
        row.timestamp?.toDate?.() || new Date(row.timestamp);
      if (
        !residentMap.has(residentName) ||
        assessmentDate >
          (residentMap.get(residentName).timestamp?.toDate?.() ||
            new Date(residentMap.get(residentName).timestamp))
      ) {
        residentMap.set(residentName, row);
      }
    });
    return Array.from(residentMap.values());
  };

  const deduplicatedHistoricalData =
    getLatestAssessmentPerResident(historicalData);
  const deduplicatedCurrentData =
    getLatestAssessmentPerResident(currentResidentData);
  const allData = [...deduplicatedHistoricalData, ...deduplicatedCurrentData];

  console.log(`📊 Final data counts after joining:`);
  console.log(
    `  Historical (with outcomes): ${deduplicatedHistoricalData.length}`
  );
  console.log(`  Current residents: ${deduplicatedCurrentData.length}`);
  console.log(`  Total: ${allData.length}`);

  return {
    rawData: joinedData, // Now includes both RC and resident data
    historicalData: deduplicatedHistoricalData,
    currentData: deduplicatedCurrentData,
    allData,
    alumniMap,
    residentsMap,
  };
};

/**
 * FOR RCRiskModel.jsx - Risk scoring and management
 */
// This uses proper statistical methods instead of additive/multiplicative hacks
const calculateRiskManagement = async (organizationId) => {
  try {
    console.log(`\n🚀 STARTING REBUILT RISK ANALYSIS for ${organizationId}`);

    // Get core data (keep your existing function)
    const coreData = await fetchCoreRCData(organizationId);
    const { historicalData, currentData, allData, alumniMap, residentsMap } =
      coreData;

    console.log(
      `📊 Data loaded: ${historicalData.length} historical, ${currentData.length} current`
    );

    // 🔥 NEW: Calculate daysSoberAtAssessment for ALL residents BEFORE protection calculation
    console.log(`🔍 PRE-CALCULATING DAYS SOBER FOR ALL RESIDENTS...`);

    const historicalDataWithDays = historicalData.map((resident, i) => {
      if (i < 3) {
        console.log(`🔍 BEFORE extraction - ${resident.residentName}:`, {
          sobrietyDate: resident.sobrietyDate,
          alumniSobrietyDate: resident.alumniSobrietyDate,
        });
      }

      const extractedData = extractResidentDisplayData(resident);

      if (i < 3) {
        console.log(`✅ AFTER extraction - ${resident.residentName}:`, {
          daysSoberAtAssessment: extractedData.daysSoberAtAssessment,
          calculatedFrom: resident.sobrietyDate || resident.alumniSobrietyDate,
        });
      }

      return {
        ...resident,
        daysSoberAtAssessment: extractedData.daysSoberAtAssessment,
        age: extractedData.age,
        // Keep other extracted fields too
        recoveryCapitalTotal: extractedData.recoveryCapitalTotal,
        residentName: extractedData.residentName,
      };
    });

    console.log(`📊 Sample days sober after extraction:`);
    historicalDataWithDays.slice(0, 3).forEach((resident, i) => {
      console.log(
        `  ${resident.residentName}: ${resident.daysSoberAtAssessment} days (${(
          resident.daysSoberAtAssessment / 365.25
        ).toFixed(1)} years)`
      );
    });

    // Now use the enhanced data for protection calculation
    const protectionData = calculateRecoveryTimeProtection(
      historicalDataWithDays, // ← Use the enhanced data!
      alumniMap
    );

    // === STEP 1: PREPARE TRAINING DATA ===
    const trainingData = prepareTrainingData(historicalDataWithDays, alumniMap); // ← Also use enhanced data here
    console.log(
      `🔍 Training data: ${trainingData.length} residents with outcomes`
    );

    if (trainingData.length < 15) {
      return {
        status: "insufficient_data",
        message: `Need at least 15 residents with outcomes (found ${trainingData.length})`,
        sampleSize: trainingData.length,
      };
    }

    // === STEP 2: TRAIN LOGISTIC REGRESSION MODEL ===
    const model = trainLogisticModel(trainingData);
    console.log(`📈 Model trained with ${model.features.length} features`);

    // === STEP 3: VALIDATE MODEL WITH PROPER CROSS-VALIDATION ===
    const validation = performCrossValidation(trainingData, 5); // 5-fold CV
    console.log(
      `✅ Cross-validation accuracy: ${(validation.accuracy * 100).toFixed(1)}%`
    );

    // === STEP 4: SCORE ALL RESIDENTS ===
    const historicalScores = trainingData.map((resident) => {
      // Match by name: residentName (from RC) with fullName (from residents)
      const originalResident = historicalDataWithDays.find((orig) => {
        const origName = (orig.fullName || orig.residentName || "")
          .trim()
          .toLowerCase();
        const resName = (resident.residentName || "").trim().toLowerCase();
        return origName === resName && origName.length > 0;
      });

      const displayData = extractResidentDisplayData(originalResident || {});
      const riskResult = scoreResident(
        resident,
        model,
        displayData,
        protectionData
      );

      console.log(`📊 Historical resident ${displayData.residentName}:`, {
        rcScore: displayData.recoveryCapitalTotal,
        daysSober: displayData.daysSoberAtAssessment,
        age: displayData.age,
        riskScore: riskResult.riskScore?.toFixed(1),
        relapsed: resident.relapsed,
      });

      return {
        ...riskResult,
        ...displayData,
        actualRelapse: resident.relapsed,
        sourceCollection: "alumni",
      };
    });

    const currentScores = currentData.map((resident) => {
      const preparedData = prepareResidentData(resident);
      const displayData = extractResidentDisplayData(resident);
      const riskResult = scoreResident(
        preparedData,
        model,
        displayData,
        protectionData
      );

      console.log(`✅ Current resident ${displayData.residentName}:`, {
        rcScore: displayData.recoveryCapitalTotal,
        daysSober: displayData.daysSoberAtAssessment,
        age: displayData.age,
        riskScore: riskResult.riskScore?.toFixed(1),
      });

      return {
        ...riskResult,
        ...displayData,
        actualRelapse: null,
        sourceCollection: "residents",
      };
    });

    const allRiskScores = [...historicalScores, ...currentScores];

    // === STEP 5: OPTIMIZE THRESHOLDS BASED ON ACTUAL PERFORMANCE ===
    const thresholds = optimizeThresholds(historicalScores);
    console.log(
      `⚖️ Optimized thresholds: High ≥${thresholds.high}%, Medium ≥${thresholds.medium}%`
    );

    // Apply thresholds
    allRiskScores.forEach((resident) => {
      if (resident.riskScore >= thresholds.high) {
        resident.riskLevel = "high";
      } else if (resident.riskScore >= thresholds.medium) {
        resident.riskLevel = "medium";
      } else {
        resident.riskLevel = "low";
      }
    });

    // === STEP 6: CALCULATE INSIGHTS (like your original banners) ===
    const insights = generateDataDrivenInsights(
      historicalDataWithDays, // ← Use enhanced data here too
      alumniMap,
      model
    );

    // === STEP 7: CALCULATE FINAL METRICS ===
    const currentResidents = allRiskScores.filter(
      (r) => r.sourceCollection === "residents"
    );
    const riskDistribution = {
      high: currentResidents.filter((r) => r.riskLevel === "high").length,
      medium: currentResidents.filter((r) => r.riskLevel === "medium").length,
      low: currentResidents.filter((r) => r.riskLevel === "low").length,
    };

    console.log(`\n🎯 FINAL RESULTS:`);
    console.log(`  Model Accuracy: ${(validation.accuracy * 100).toFixed(1)}%`);
    console.log(`  High Risk: ${riskDistribution.high} residents`);
    console.log(`  Medium Risk: ${riskDistribution.medium} residents`);
    console.log(`  Low Risk: ${riskDistribution.low} residents`);

    return {
      riskStratification: {
        riskScores: allRiskScores,
        riskDistribution,
        historicalAccuracy: validation.accuracy,
        thresholds,
      },
      systemStatus: {
        status:
          validation.accuracy > 0.7
            ? "excellent"
            : validation.accuracy > 0.6
            ? "good"
            : validation.accuracy > 0.5
            ? "needs_improvement"
            : "poor",
        accuracy: validation.accuracy,
        readyForProduction: validation.accuracy > 0.5,
        method: "logistic-regression",
        sampleSize: trainingData.length,
        recommendation:
          validation.accuracy > 0.7
            ? "Model performing excellently"
            : validation.accuracy > 0.6
            ? "Model performing well"
            : validation.accuracy > 0.5
            ? "Model needs improvement - consider collecting more data"
            : "Model accuracy too low for production use",
      },
      // MATCH REACT COMPONENT EXPECTATIONS
      temporalValidation: {
        status:
          validation.accuracy > 0.7
            ? "validated"
            : validation.accuracy > 0.6
            ? "preliminary_validation"
            : "building_dataset",
        averageAccuracy: validation.accuracy,
        recommendation:
          validation.accuracy > 0.7
            ? "Model performing excellently"
            : validation.accuracy > 0.6
            ? "Model performing well"
            : validation.accuracy > 0.5
            ? "Model needs improvement - consider collecting more data"
            : "Model accuracy too low for production use",
        sampleSize: trainingData.length,
        results: [
          {
            splitPoint: 0.7,
            trainPeriod: { start: "Historical", end: "Training" },
            testPeriod: { start: "Cross-Val", end: "Testing" },
            trainSize: Math.floor(trainingData.length * 0.8),
            testSize: Math.floor(trainingData.length * 0.2),
            accuracy: validation.accuracy,
            correctPredictions: Math.floor(
              validation.accuracy * Math.floor(trainingData.length * 0.2)
            ),
            totalPredictions: Math.floor(trainingData.length * 0.2),
          },
        ],
      },
      learnedPatterns: {
        metadata: {
          highestRiskPeriod: insights.temporalRisk
            ? {
                range: insights.temporalRisk.period,
                relapseRate: insights.temporalRisk.relapseRate,
              }
            : null,
          sampleSize: trainingData.length,
        },
        rcThresholds: insights.rcThreshold
          ? {
              low: insights.rcThreshold.threshold,
              medium: insights.rcThreshold.threshold + 20,
              high: insights.rcThreshold.threshold + 40,
            }
          : { low: 138, medium: 158, high: 178 },
        riskFactorWeights: model.features.reduce((acc, feature) => {
          acc[feature] = {
            coefficient: model.coefficients[feature],
            impact:
              Math.abs(model.coefficients[feature]) > 0.5 ? "high" : "moderate",
          };
          return acc;
        }, {}),
      },
      // ADD RISK METRICS FOR THE UI
      riskMetrics: model.features.reduce((acc, feature) => {
        const coeff = model.coefficients[feature];
        acc[feature] = {
          or: Math.exp(coeff), // Convert logistic coefficient to odds ratio
          rateExp: 50 + coeff * 20, // Approximate percentage for display
          rateRef: 50 - coeff * 10, // Reference rate for display
          strength: Math.abs(coeff) > 0.5 ? "high" : "moderate",
          sampleSizes: { with: 30, without: 30 }, // Placeholder
          significant: Math.abs(coeff) > 0.3,
        };
        return acc;
      }, {}),
      modelValidation: validation,
      insights: insights,
      // ADD VALIDATION WARNINGS FOR UI BANNERS
      validationWarnings:
        trainingData.length < 50
          ? {
              smallSample: true,
              sampleSize: trainingData.length,
            }
          : null,
      rcData: {
        totalUniqueIndividuals: allData.length,
        assessmentCount: coreData.rawData.length,
        historicalCount: historicalData.length,
        currentCount: currentData.length,
      },
      recoveryTimeProtection: protectionData,
    };
  } catch (error) {
    console.error("❌ Error in risk management:", error);
    throw error;
  }
};

// === HELPER FUNCTIONS ===
const prepareTrainingData = (historicalData, alumniMap) => {
  const trainingData = [];

  historicalData.forEach((resident) => {
    const residentName =
      resident.residentName ||
      resident.fullName ||
      `${resident.firstName} ${resident.lastName}`.trim();

    const alumni = alumniMap.get(residentName);

    if (alumni && alumni.sobrietyStatus) {
      const relapsed = alumni.sobrietyStatus === "Not Sober";

      trainingData.push({
        ...prepareResidentData(resident), // Use old version for initial filtering
        relapsed: relapsed ? 1 : 0,
        residentName,
      });
    }
  });

  return trainingData;
};

const prepareResidentData = (
  resident,
  validFactors = null,
  validCombinations = null
) => {
  // If no filtering info provided, use old hardcoded approach for backwards compatibility
  if (!validFactors) {
    return {
      trauma: checkResponse(resident.childhoodTrauma) === "yes" ? 1 : 0,
      cod: checkResponse(resident.coOccurringDiagnosis) === "yes" ? 1 : 0,
      prison: checkResponse(resident.overOneYearInPrison) === "yes" ? 1 : 0,
      homeless: checkResponse(resident.homelessOneYear) === "yes" ? 1 : 0,
      singleParent: checkResponse(resident.twoParentsHome) === "no" ? 1 : 0,
      rcTotal: (+resident.recoveryCapitalTotal || 0) / 200,
      age: (+resident.age || 30) / 60,
      daysSoberYears: (+resident.daysSoberAtAssessment || 1) / 365.25,
      traumaAndCod:
        checkResponse(resident.childhoodTrauma) === "yes" &&
        checkResponse(resident.coOccurringDiagnosis) === "yes"
          ? 1
          : 0,
      prisonAndHomeless:
        checkResponse(resident.overOneYearInPrison) === "yes" &&
        checkResponse(resident.homelessOneYear) === "yes"
          ? 1
          : 0,
      residentName:
        resident.residentName ||
        resident.fullName ||
        `${resident.firstName} ${resident.lastName}`.trim(),
    };
  }

  // NEW: Dynamic feature preparation based on filtering results
  const features = {
    // Always include continuous variables (they don't have prevalence issues)
    rcTotal: (+resident.recoveryCapitalTotal || 0) / 200,
    age: (+resident.age || 30) / 60,
    daysSoberYears: (+resident.daysSoberAtAssessment || 1) / 365.25,
  };

  // Add individual factors that passed filtering
  const factorMapping = {
    trauma: "childhoodTrauma",
    cod: "coOccurringDiagnosis",
    prison: "overOneYearInPrison",
    homeless: "homelessOneYear",
    singleParent: "twoParentsHome",
  };

  validFactors.forEach((factor) => {
    if (factor === "singleParent") {
      features[factor] =
        checkResponse(resident[factorMapping[factor]]) === "no" ? 1 : 0;
    } else {
      features[factor] =
        checkResponse(resident[factorMapping[factor]]) === "yes" ? 1 : 0;
    }
  });

  // Add combinations that passed filtering
  validCombinations.forEach((combo) => {
    features[combo.name] = combo.factors.every(
      (factor) => features[factor] === 1
    )
      ? 1
      : 0;
  });

  features.residentName =
    resident.residentName ||
    resident.fullName ||
    `${resident.firstName} ${resident.lastName}`.trim();

  return features;
};

// ===== FIX #2: ADD THIS NEW FUNCTION BEFORE trainLogisticModel =====
const filterFactorsAndCombinations = (trainingData) => {
  const baseFactors = ["trauma", "cod", "prison", "homeless", "singleParent"];
  const validFactors = [];
  const validCombinations = [];

  console.log("\n🔍 TESTING INDIVIDUAL FACTORS FOR ADEQUATE SAMPLE SIZES...");

  // Test individual factors first
  baseFactors.forEach((factor) => {
    const withFactor = trainingData.filter((r) => r[factor] === 1);
    const withoutFactor = trainingData.filter((r) => r[factor] === 0);
    const prevalence = withFactor.length / trainingData.length;

    console.log(
      `  ${factor}: With=${withFactor.length}, Without=${
        withoutFactor.length
      }, Prevalence=${(prevalence * 100).toFixed(1)}%`
    );

    // Check if both groups have adequate sample sizes AND prevalence is reasonable
    if (
      withFactor.length >= MINIMUM_SAMPLE_SIZE &&
      withoutFactor.length >= MINIMUM_SAMPLE_SIZE &&
      prevalence >= MINIMUM_PREVALENCE &&
      prevalence <= MAXIMUM_PREVALENCE
    ) {
      validFactors.push(factor);
      console.log(`    ✅ INCLUDED`);
    } else {
      const reason =
        withFactor.length < MINIMUM_SAMPLE_SIZE
          ? "insufficient 'with' sample"
          : withoutFactor.length < MINIMUM_SAMPLE_SIZE
          ? "insufficient 'without' sample"
          : prevalence > MAXIMUM_PREVALENCE
          ? `too high prevalence (${(prevalence * 100).toFixed(1)}%)`
          : prevalence < MINIMUM_PREVALENCE
          ? `too low prevalence (${(prevalence * 100).toFixed(1)}%)`
          : "unknown";
      console.log(`    ❌ EXCLUDED: ${reason}`);
    }
  });

  console.log(
    `\n🔍 TESTING ALL POSSIBLE COMBINATIONS FOR ADEQUATE SAMPLE SIZES...`
  );

  // Test all possible combinations (only using valid factors)
  if (validFactors.length >= 2) {
    for (let i = 1; i < Math.pow(2, validFactors.length); i++) {
      // Start at 1 to skip "no factors"
      const bitmask = i.toString(2).padStart(validFactors.length, "0");
      const factorsInCombo = validFactors.filter(
        (_, index) => bitmask[index] === "1"
      );

      if (factorsInCombo.length > 3) continue; // Skip 4+ factor combinations (too complex)

      const comboName = factorsInCombo.join("And");

      // Count people who have AT LEAST these factors (may have more)
      const withCombo = trainingData.filter((resident) =>
        factorsInCombo.every((factor) => resident[factor] === 1)
      );

      // Count people who DON'T have this complete combination
      const withoutCombo = trainingData.filter(
        (resident) => !factorsInCombo.every((factor) => resident[factor] === 1)
      );

      const withRelapses = withCombo.filter((r) => r.relapsed === 1).length;
      const withoutRelapses = withoutCombo.filter(
        (r) => r.relapsed === 1
      ).length;

      const withRate =
        withCombo.length > 0 ? withRelapses / withCombo.length : 0;
      const withoutRate =
        withoutCombo.length > 0 ? withoutRelapses / withoutCombo.length : 0;
      const riskDifference = withRate - withoutRate;

      console.log(
        `  Testing ${comboName}: With=${withCombo.length}, Without=${
          withoutCombo.length
        }, RiskDiff=${(riskDifference * 100).toFixed(1)}pp`
      );

      // Only include if both groups have adequate sample sizes AND meaningful difference
      if (
        withCombo.length >= MINIMUM_SAMPLE_SIZE &&
        withoutCombo.length >= MINIMUM_SAMPLE_SIZE &&
        Math.abs(riskDifference) > 0.15
      ) {
        // At least 5% difference

        validCombinations.push({
          name: comboName,
          factors: factorsInCombo,
          withCount: withCombo.length,
          withoutCount: withoutCombo.length,
          riskDifference: riskDifference,
        });

        console.log(
          `    ✅ INCLUDED: ${comboName} (${(riskDifference * 100).toFixed(
            1
          )}pp difference)`
        );
      } else {
        const reason =
          withCombo.length < MINIMUM_SAMPLE_SIZE
            ? "insufficient 'with' sample"
            : withoutCombo.length < MINIMUM_SAMPLE_SIZE
            ? "insufficient 'without' sample"
            : "insufficient risk difference";
        console.log(`    ❌ EXCLUDED: ${comboName} (${reason})`);
      }
    }
  }

  console.log(`\n📊 FILTERING RESULTS:`);
  console.log(
    `  Valid individual factors: ${validFactors.length} out of ${baseFactors.length}`
  );
  console.log(`  Valid combinations: ${validCombinations.length}`);
  console.log(`  Individual factors: ${validFactors.join(", ") || "None"}`);
  console.log(
    `  Combinations: ${
      validCombinations.map((c) => c.name).join(", ") || "None"
    }`
  );

  return { validFactors, validCombinations };
};

const trainLogisticModel = (trainingData) => {
  // NEW: Filter factors and combinations by sample size BEFORE training
  const filterResults = filterFactorsAndCombinations(trainingData);
  const { validFactors, validCombinations } = filterResults;

  // Build feature list dynamically
  const features = [
    ...validFactors,
    "rcTotal",
    "age",
    "daysSoberYears",
    ...validCombinations.map((c) => c.name),
  ];

  console.log(`\n🧠 TRAINING LOGISTIC MODEL WITH ${features.length} FEATURES:`);
  features.forEach((name) => console.log(`  • ${name}`));

  if (features.length < 3) {
    console.log("⚠️ Too few valid features for reliable model training");
    return {
      coefficients: {},
      features: [],
      allFeatures: features,
      validFactors,
      validCombinations,
      filterResults,
    };
  }

  // Recreate training data with only valid features
  const filteredTrainingData = trainingData.map((resident) => {
    const originalResident = {
      childhoodTrauma: resident.trauma === 1 ? "yes" : "no",
      coOccurringDiagnosis: resident.cod === 1 ? "yes" : "no",
      overOneYearInPrison: resident.prison === 1 ? "yes" : "no",
      homelessOneYear: resident.homeless === 1 ? "yes" : "no",
      twoParentsHome: resident.singleParent === 1 ? "no" : "yes",
      recoveryCapitalTotal: resident.rcTotal * 200,
      age: resident.age * 60,
      daysSoberAtAssessment: Math.exp(resident.daysSober * 10) - 1,
      residentName: resident.residentName,
    };

    return {
      ...prepareResidentData(originalResident, validFactors, validCombinations),
      relapsed: resident.relapsed,
    };
  });

  // Initialize coefficients for valid features only
  let coefficients = {};
  features.forEach((feature) => {
    coefficients[feature] = 0;
  });
  coefficients.intercept = 0;

  const learningRate = 0.1;
  const iterations = 1000;

  // Gradient descent (same logic, but with dynamic features)
  for (let iter = 0; iter < iterations; iter++) {
    const gradients = {};
    features.forEach((feature) => (gradients[feature] = 0));
    gradients.intercept = 0;

    filteredTrainingData.forEach((resident) => {
      let linearCombination = coefficients.intercept;
      features.forEach((feature) => {
        linearCombination += coefficients[feature] * (resident[feature] || 0);
      });

      const prediction = 1 / (1 + Math.exp(-linearCombination));
      const error = prediction - resident.relapsed;

      gradients.intercept += error;
      features.forEach((feature) => {
        gradients[feature] += error * (resident[feature] || 0);
      });
    });

    // Update coefficients
    coefficients.intercept -=
      (learningRate * gradients.intercept) / filteredTrainingData.length;
    features.forEach((feature) => {
      coefficients[feature] -=
        (learningRate * gradients[feature]) / filteredTrainingData.length;
    });
  }

  // Filter out features with minimal impact
  const significantFeatures = features;

  console.log(`📊 Significant features: ${significantFeatures.join(", ")}`);
  significantFeatures.forEach((feature) => {
    const direction = coefficients[feature] > 0 ? "increases" : "decreases";
    console.log(
      `  ${feature}: ${coefficients[feature].toFixed(3)} (${direction} risk)`
    );
  });

  return {
    coefficients,
    features: significantFeatures,
    allFeatures: features,
    validFactors,
    validCombinations,
    filterResults,
  };
};

/**
 * Enhanced scoring function with robust recovery time protection
 */
const scoreResident = (
  residentData,
  model,
  originalResident = null,
  protectionData = null
) => {
  console.log(`\n🔍 DETAILED RISK SCORING FOR: ${residentData.residentName}`);

  // 🔥 ADD THIS DEBUG BLOCK:
  console.log(`🛡️ DEBUG originalResident:`, {
    hasOriginalResident: !!originalResident,
    daysSoberAtAssessment: originalResident?.daysSoberAtAssessment,
    daysSober: originalResident?.daysSober,
    recoveryTime: originalResident?.recoveryTime,
    allFields: originalResident ? Object.keys(originalResident) : "none",
  });

  let linearCombination = model.coefficients.intercept;
  console.log(
    `🔢 Starting with intercept: ${model.coefficients.intercept.toFixed(3)}`
  );

  // Calculate base model contribution
  const contributionBreakdown = [];
  model.allFeatures.forEach((feature) => {
    if (residentData.hasOwnProperty(feature)) {
      const value = residentData[feature] || 0;
      const coefficient = model.coefficients[feature];
      const contribution = coefficient * value;

      contributionBreakdown.push({
        feature,
        value,
        coefficient: coefficient.toFixed(3),
        contribution: contribution.toFixed(3),
        active: value > 0,
      });

      linearCombination += contribution;

      if (Math.abs(contribution) > 0.01) {
        const direction = contribution > 0 ? "⬆️ INCREASES" : "⬇️ DECREASES";
        console.log(
          `  ${feature}: ${value} × ${coefficient.toFixed(
            3
          )} = ${contribution.toFixed(3)} ${direction} risk`
        );
      }
    }
  });

  // 🔥 NEW: ROBUST RECOVERY TIME PROTECTION
  const daysSoberAtAssessment = originalResident?.daysSoberAtAssessment || 0;
  let recoveryTimeProtection = 0;
  let protectionExplanation = "";
  let protectionReliability = "none";

  if (protectionData && daysSoberAtAssessment > 0) {
    const protection = getRecoveryTimeProtection(
      daysSoberAtAssessment,
      protectionData
    );

    // Convert protection factor to risk reduction
    // Scale by severity: higher protection factors get more risk reduction
    if (protection.protectionFactor > 0.2) {
      recoveryTimeProtection = -2.0; // Strong protection
      protectionReliability = "strong";
    } else if (protection.protectionFactor > 0.1) {
      recoveryTimeProtection = -1.2; // Moderate protection
      protectionReliability = "moderate";
    } else if (protection.protectionFactor > 0.05) {
      recoveryTimeProtection = -0.6; // Mild protection
      protectionReliability = "mild";
    } else {
      recoveryTimeProtection = -0.2; // Minimal protection
      protectionReliability = "minimal";
    }

    // Adjust for reliability
    if (protection.fallbackUsed) {
      recoveryTimeProtection *= 0.7; // Reduce protection if using fallback
      protectionReliability += " (fallback)";
    }

    const recoveryYears = daysSoberAtAssessment / 365.25;
    protectionExplanation = `🛡️ ${protectionReliability.toUpperCase()} PROTECTION: ${recoveryYears.toFixed(
      1
    )} years (${protection.bucket})`;

    console.log(`🛡️ Recovery Protection Analysis:`);
    console.log(`  Days sober: ${daysSoberAtAssessment}`);
    console.log(`  Time bucket: ${protection.bucket}`);
    console.log(
      `  Protection factor: ${(protection.protectionFactor * 100).toFixed(1)}%`
    );
    console.log(`  Risk reduction: ${recoveryTimeProtection.toFixed(3)}`);
    console.log(`  Reliability: ${protection.reliable ? "HIGH" : "FALLBACK"}`);
    if (protection.fallbackUsed) {
      console.log(`  Fallback source: ${protection.fallbackSource}`);
    }
  } else if (daysSoberAtAssessment > 0) {
    // Fallback to simple time-based protection if no data available
    const recoveryYears = daysSoberAtAssessment / 365.25;
    if (recoveryYears >= 2.0) {
      recoveryTimeProtection = -1.5;
      protectionExplanation = `🛡️ STRONG PROTECTION: ${recoveryYears.toFixed(
        1
      )} years (default)`;
    } else if (recoveryYears >= 1.0) {
      recoveryTimeProtection = -0.8;
      protectionExplanation = `🛡️ MODERATE PROTECTION: ${recoveryYears.toFixed(
        1
      )} years (default)`;
    } else if (recoveryYears >= 0.5) {
      recoveryTimeProtection = -0.3;
      protectionExplanation = `🛡️ MILD PROTECTION: ${recoveryYears.toFixed(
        1
      )} years (default)`;
    }
    console.log(
      `🛡️ Using default protection: ${recoveryTimeProtection.toFixed(3)}`
    );
  }

  // Apply recovery time protection
  linearCombination += recoveryTimeProtection;

  const probability = 1 / (1 + Math.exp(-linearCombination));
  const riskScore = Math.round(probability * 100);

  console.log(`🧮 Final Calculation:`);
  console.log(
    `  Base linear combination: ${(
      linearCombination - recoveryTimeProtection
    ).toFixed(3)}`
  );
  if (recoveryTimeProtection !== 0) {
    console.log(
      `  Recovery time protection: ${recoveryTimeProtection.toFixed(3)}`
    );
  }
  console.log(`  Final linear combination: ${linearCombination.toFixed(3)}`);
  console.log(`  Probability: ${probability.toFixed(3)}`);
  console.log(`  Risk Score: ${riskScore}%`);

  // Build active factors list
  const factorNames = {
    trauma: "Childhood Trauma",
    cod: "Co-occurring Diagnosis",
    prison: "Prison Experience",
    homeless: "Housing Instability",
    singleParent: "Single Parent Household",
  };

  const activeFactors = [];

  // Add risk factors
  (model.features || []).forEach((feature) => {
    if (residentData[feature] === 1 && model.coefficients[feature] > 0.1) {
      activeFactors.push(factorNames[feature] || feature);
    }
  });

  // Add protection status
  if (protectionExplanation) {
    activeFactors.push(protectionExplanation);
  }

  // Extract display values
  let recoveryCapitalScore = 0;
  if (originalResident) {
    recoveryCapitalScore = +originalResident.recoveryCapitalTotal || 0;
  } else if (residentData.rcTotal !== undefined) {
    recoveryCapitalScore = Math.round(residentData.rcTotal * 200);
  }

  console.log(`📋 Final Display Values:`);
  console.log(`  RC Score: ${recoveryCapitalScore}`);
  console.log(`  Days Sober: ${daysSoberAtAssessment}`);
  console.log(`  Risk Score: ${riskScore}%`);
  console.log(`  Protection: ${protectionReliability}`);

  return {
    riskScore,
    riskLevel: "calculated_later",
    activeFactors,
    residentName: residentData.residentName,
    probability: probability,
    recoveryCapitalScore: recoveryCapitalScore,
    daysSoberAtAssessment: daysSoberAtAssessment,
    rcScore: recoveryCapitalScore,
    recoveryTime: daysSoberAtAssessment,
    debugInfo: {
      linearCombination,
      baseLinearCombination: linearCombination - recoveryTimeProtection,
      recoveryTimeProtection,
      protectionReliability,
      daysSober: daysSoberAtAssessment,
      contributionBreakdown,
      inputData: residentData,
      protectionExplanation,
    },
  };
};

const performCrossValidation = (data, folds = 5) => {
  const shuffled = [...data].sort(() => Math.random() - 0.5);
  const foldSize = Math.floor(shuffled.length / folds);

  let totalAccuracy = 0;
  let totalPrecision = 0;
  let totalRecall = 0;

  for (let i = 0; i < folds; i++) {
    const start = i * foldSize;
    const end = i === folds - 1 ? shuffled.length : start + foldSize;

    const testData = shuffled.slice(start, end);
    const trainData = [...shuffled.slice(0, start), ...shuffled.slice(end)];

    if (trainData.length < 5) continue;

    const model = trainLogisticModel(trainData);

    let correct = 0;
    let truePositives = 0;
    let falsePositives = 0;
    let falseNegatives = 0;

    testData.forEach((resident) => {
      const scored = scoreResident(resident, model);
      const predicted = scored.riskScore >= 50 ? 1 : 0;
      const actual = resident.relapsed;

      if (predicted === actual) correct++;
      if (predicted === 1 && actual === 1) truePositives++;
      if (predicted === 1 && actual === 0) falsePositives++;
      if (predicted === 0 && actual === 1) falseNegatives++;
    });

    const accuracy = correct / testData.length;
    const precision = truePositives / (truePositives + falsePositives) || 0;
    const recall = truePositives / (truePositives + falseNegatives) || 0;

    totalAccuracy += accuracy;
    totalPrecision += precision;
    totalRecall += recall;
  }

  const avgAccuracy = totalAccuracy / folds;
  const avgPrecision = totalPrecision / folds;
  const avgRecall = totalRecall / folds;
  const f1Score =
    (2 * (avgPrecision * avgRecall)) / (avgPrecision + avgRecall) || 0;

  return {
    accuracy: avgAccuracy,
    precision: avgPrecision,
    recall: avgRecall,
    f1Score: f1Score,
    method: "k-fold-cross-validation",
    folds: folds,
  };
};

const optimizeThresholds = (historicalScores) => {
  // Find thresholds that maximize F1 score
  const scores = historicalScores.map((r) => r.riskScore).sort((a, b) => a - b);
  let bestF1 = 0;
  let bestThresholds = { high: 70, medium: 50 };

  for (let high = 40; high <= 80; high += 5) {
    for (let medium = 25; medium <= high - 5; medium += 5) {
      const classified = historicalScores.map((resident) => ({
        predicted:
          resident.riskScore >= high ? 2 : resident.riskScore >= medium ? 1 : 0,
        actual: resident.actualRelapse ? 1 : 0,
      }));

      // Calculate metrics for high-risk classification
      const highRiskPredicted = classified.filter((c) => c.predicted === 2);
      const highRiskActual = classified.filter((c) => c.actual === 1);

      const truePositives = classified.filter(
        (c) => c.predicted === 2 && c.actual === 1
      ).length;
      const falsePositives = classified.filter(
        (c) => c.predicted === 2 && c.actual === 0
      ).length;
      const falseNegatives = classified.filter(
        (c) => c.predicted < 2 && c.actual === 1
      ).length;

      const precision = truePositives / (truePositives + falsePositives) || 0;
      const recall = truePositives / (truePositives + falseNegatives) || 0;
      const f1 = (2 * (precision * recall)) / (precision + recall) || 0;

      if (f1 > bestF1) {
        bestF1 = f1;
        bestThresholds = { high, medium };
      }
    }
  }

  console.log(
    `🎯 Threshold optimization: F1=${bestF1.toFixed(3)}, High≥${
      bestThresholds.high
    }%, Med≥${bestThresholds.medium}%`
  );

  return bestThresholds;
};

const generateDataDrivenInsights = (historicalData, alumniMap, model) => {
  console.log("\n🔍 GENERATING DATA-DRIVEN INSIGHTS...");

  // === TEMPORAL RISK ANALYSIS ===
  const timeperiods = [
    { name: "0-90 days", min: 0, max: 90 },
    { name: "91-180 days", min: 91, max: 180 },
    { name: "181-270 days", min: 181, max: 270 },
    { name: "271-365 days", min: 271, max: 365 },
    { name: "366-540 days", min: 366, max: 540 },
    { name: "541-730 days", min: 541, max: 730 },
    { name: "731+ days", min: 731, max: 99999 },
  ];

  let highestRiskPeriod = null;
  let highestRiskRate = 0;

  // 🔥 ADD DEBUG OUTPUT FOR ALL PERIODS
  console.log("\n📊 TEMPORAL RISK ANALYSIS BY PERIOD:");

  timeperiods.forEach((period) => {
    const residentsInPeriod = historicalData.filter((resident) => {
      const daysSober =
        +resident.daysSoberAtAssessment ||
        +resident.daysSober ||
        +resident.soberDays ||
        30; // Default fallback
      return daysSober >= period.min && daysSober <= period.max;
    });

    const relapses = residentsInPeriod.filter((resident) => {
      const residentName =
        resident.residentName ||
        resident.fullName ||
        `${resident.firstName} ${resident.lastName}`.trim();
      const alumni = alumniMap.get(residentName);
      return alumni && alumni.sobrietyStatus === "Not Sober";
    }).length;

    const relapseRate =
      residentsInPeriod.length > 0 ? relapses / residentsInPeriod.length : 0;

    // 🔥 DEBUG: Show ALL periods
    console.log(
      `  ${period.name}: ${
        residentsInPeriod.length
      } people, ${relapses} relapses, ${(relapseRate * 100).toFixed(1)}% rate ${
        residentsInPeriod.length >= 15 ? "✅" : "❌ TOO SMALL"
      }`
    );

    // 🔥 CHANGE: Require at least 15 people (was 5)
    if (residentsInPeriod.length >= 15) {
      if (relapseRate > highestRiskRate) {
        highestRiskRate = relapseRate;
        highestRiskPeriod = {
          period: period.name,
          relapseRate: relapseRate,
          count: residentsInPeriod.length,
          relapses: relapses,
        };
      }
    }
  });

  // 🔥 ADD: Fallback if no period has 15+ people
  if (!highestRiskPeriod) {
    console.log("⚠️ No period has 15+ people, using largest sample size...");

    let largestSample = null;
    let largestSampleSize = 0;

    timeperiods.forEach((period) => {
      const residentsInPeriod = historicalData.filter((resident) => {
        const daysSober =
          +resident.daysSoberAtAssessment ||
          +resident.daysSober ||
          +resident.soberDays ||
          30;
        return daysSober >= period.min && daysSober <= period.max;
      });

      if (
        residentsInPeriod.length > largestSampleSize &&
        residentsInPeriod.length >= 5
      ) {
        const relapses = residentsInPeriod.filter((resident) => {
          const residentName =
            resident.residentName ||
            resident.fullName ||
            `${resident.firstName} ${resident.lastName}`.trim();
          const alumni = alumniMap.get(residentName);
          return alumni && alumni.sobrietyStatus === "Not Sober";
        }).length;

        largestSampleSize = residentsInPeriod.length;
        largestSample = {
          period: period.name,
          relapseRate: relapses / residentsInPeriod.length,
          count: residentsInPeriod.length,
          relapses: relapses,
        };
      }
    });

    highestRiskPeriod = largestSample;
    console.log(
      `📊 Using largest sample: ${highestRiskPeriod?.period} (${highestRiskPeriod?.count} people)`
    );
  }

  // === RECOVERY CAPITAL THRESHOLD ANALYSIS ===
  // 🔥 GENERATE DATA-DRIVEN THRESHOLDS instead of hardcoded ones
  const rcScores = historicalData
    .map((r) => +r.recoveryCapitalTotal || 0)
    .filter((score) => score > 0)
    .sort((a, b) => a - b);

  console.log(
    `📊 RC Score Distribution: Min=${Math.min(...rcScores)}, Max=${Math.max(
      ...rcScores
    )}, Count=${rcScores.length}`
  );

  // Use percentile-based thresholds from YOUR actual data
  const rcThresholds =
    rcScores.length >= 20
      ? [
          rcScores[Math.floor(rcScores.length * 0.2)], // 20th percentile
          rcScores[Math.floor(rcScores.length * 0.33)], // 33rd percentile
          rcScores[Math.floor(rcScores.length * 0.5)], // Median (50th)
          rcScores[Math.floor(rcScores.length * 0.67)], // 67th percentile
          rcScores[Math.floor(rcScores.length * 0.8)], // 80th percentile
        ]
      : [120, 140, 160, 180, 200]; // Fallback if too little data

  console.log(`📊 Data-driven RC thresholds:`, rcThresholds);

  let bestRcThreshold = null;
  let bestRcDifference = 0;

  // 🔥 ADD DEBUG FOR RC THRESHOLDS
  console.log("\n📊 RC THRESHOLD ANALYSIS:");

  rcThresholds.forEach((threshold) => {
    const below = historicalData.filter(
      (r) => (+r.recoveryCapitalTotal || 0) < threshold
    );
    const above = historicalData.filter(
      (r) => (+r.recoveryCapitalTotal || 0) >= threshold
    );

    // Require at least 15 people in each group
    if (below.length >= 15 && above.length >= 15) {
      const belowRelapse =
        below.filter((resident) => {
          const residentName =
            resident.residentName ||
            resident.fullName ||
            `${resident.firstName} ${resident.lastName}`.trim();
          const alumni = alumniMap.get(residentName);
          return alumni && alumni.sobrietyStatus === "Not Sober";
        }).length / below.length;

      const aboveRelapse =
        above.filter((resident) => {
          const residentName =
            resident.residentName ||
            resident.fullName ||
            `${resident.firstName} ${resident.lastName}`.trim();
          const alumni = alumniMap.get(residentName);
          return alumni && alumni.sobrietyStatus === "Not Sober";
        }).length / above.length;

      const difference = belowRelapse - aboveRelapse;

      // DEBUG: Show all thresholds
      console.log(
        `  RC ${threshold}: Below=${below.length} (${(
          belowRelapse * 100
        ).toFixed(1)}%), Above=${above.length} (${(aboveRelapse * 100).toFixed(
          1
        )}%), Diff=${(difference * 100).toFixed(1)}pp ${
          difference > 0.1 ? "✅" : "❌"
        }`
      );

      if (difference > bestRcDifference) {
        bestRcDifference = difference;
        bestRcThreshold = {
          threshold: threshold,
          belowRate: belowRelapse,
          aboveRate: aboveRelapse,
          difference: difference,
          belowCount: below.length,
          aboveCount: above.length,
        };
      }
    } else {
      console.log(
        `  RC ${threshold}: Below=${below.length}, Above=${above.length} ❌ TOO SMALL`
      );
    }
  });

  // 🔥 ADD DEBUG FOR RC THRESHOLDS
  console.log("\n📊 RC THRESHOLD ANALYSIS:");

  rcThresholds.forEach((threshold) => {
    const below = historicalData.filter(
      (r) => (+r.recoveryCapitalTotal || 0) < threshold
    );
    const above = historicalData.filter(
      (r) => (+r.recoveryCapitalTotal || 0) >= threshold
    );

    // 🔥 CHANGE: Require at least 15 people in each group (was 10)
    if (below.length >= 15 && above.length >= 10) {
      const belowRelapse =
        below.filter((resident) => {
          const residentName =
            resident.residentName ||
            resident.fullName ||
            `${resident.firstName} ${resident.lastName}`.trim();
          const alumni = alumniMap.get(residentName);
          return alumni && alumni.sobrietyStatus === "Not Sober";
        }).length / below.length;

      const aboveRelapse =
        above.filter((resident) => {
          const residentName =
            resident.residentName ||
            resident.fullName ||
            `${resident.firstName} ${resident.lastName}`.trim();
          const alumni = alumniMap.get(residentName);
          return alumni && alumni.sobrietyStatus === "Not Sober";
        }).length / above.length;

      const difference = belowRelapse - aboveRelapse;

      // 🔥 DEBUG: Show all thresholds
      console.log(
        `  RC ${threshold}: Below=${below.length} (${(
          belowRelapse * 100
        ).toFixed(1)}%), Above=${above.length} (${(aboveRelapse * 100).toFixed(
          1
        )}%), Diff=${(difference * 100).toFixed(1)}pp ${
          difference > 0.1 ? "✅" : "❌"
        }`
      );

      if (difference > bestRcDifference) {
        bestRcDifference = difference;
        bestRcThreshold = {
          threshold: threshold,
          belowRate: belowRelapse,
          aboveRate: aboveRelapse,
          difference: difference,
          belowCount: below.length,
          aboveCount: above.length,
        };
      }
    } else {
      console.log(
        `  RC ${threshold}: Below=${below.length}, Above=${above.length} ❌ TOO SMALL`
      );
    }
  });

  // === COUNT SIGNIFICANT RISK FACTORS ===
  const significantFactors = model.features.filter(
    (feature) => Math.abs(model.coefficients[feature]) > 0.3
  ).length;

  console.log(`\n📊 FINAL INSIGHTS:`);
  if (highestRiskPeriod) {
    console.log(
      `  Highest risk period: ${highestRiskPeriod.period} (${(
        highestRiskPeriod.relapseRate * 100
      ).toFixed(1)}% relapse rate, ${highestRiskPeriod.count} people)`
    );
  } else {
    console.log(`  ⚠️ No valid highest risk period found`);
  }
  if (bestRcThreshold) {
    console.log(
      `  RC threshold: Below ${bestRcThreshold.threshold} shows ${(
        bestRcThreshold.difference * 100
      ).toFixed(1)}pp higher risk`
    );
  } else {
    console.log(`  ⚠️ No valid RC threshold found`);
  }
  console.log(`  Significant risk factors: ${significantFactors}`);

  return {
    temporalRisk: highestRiskPeriod,
    rcThreshold: bestRcThreshold,
    significantFactorCount: significantFactors,
    modelCoefficients: model.coefficients,
  };
};

// Helper function for days sober calculation
const calculateDaysSoberAtAssessment = (resident) => {
  // You'll need to implement this based on your data structure
  // This is a placeholder that tries to find the field
  return (
    +resident.daysSoberAtAssessment ||
    +resident.daysSober ||
    +resident.soberDays ||
    30
  ); // Default fallback
};

// Helper function you already have
const checkResponse = (value) => {
  if (!value || value === "" || value === "N/A" || value === "n/a") return "na";
  const normalized = value.toString().toLowerCase().trim();
  if (
    normalized === "yes" ||
    normalized === "y" ||
    normalized === "true" ||
    normalized === "1"
  )
    return "yes";
  if (
    normalized === "no" ||
    normalized === "n" ||
    normalized === "false" ||
    normalized === "0"
  )
    return "no";
  return "na";
};

// Export the main function
export { calculateRiskManagement };
